import forOwn from 'lodash/forOwn';
import { ReactElement, useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import styled from 'styled-components';
import {
  useDispatch, useSelector,
} from 'react-redux';

import {
  actions as feedActions,
} from 'Slice/FeedActivitiesSlice';
import { selectFeedBoardPulseData, selectFeedBoardPulseLoading } from 'Selector/FeedActivitiesSelector';
import { CompaniesPulse, Heatmap } from 'Types/FeedActivitiesTypes';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import classNames from 'classnames';
import HeatMapTooltipItem from 'Components/common/components/HeatMapTooltipItem';
import HeadingWithToolTip from 'Components/common/components/HeadingWithToolTip';
import { TOOLTIP_PULSE, VIDEO_PULSE } from 'Utils/Constants';
import { IsMobile } from 'Utils/UtilityFunctions';
import {
  CaretDownOutlined,
  CaretUpOutlined,
} from '@ant-design/icons';

import { actions as certificationActions } from 'Slice/CertificationSlice';

import { selectData } from 'Selector/CertificationSelector';
import {
  boxShadow, greyColor, maxMobile, maxTablet,
  xmMobile,
  primaryColor, primaryFont, secondaryFont, wMedium,
} from '../../../Utils/utilsStyle';
import PopoverButton from '../../common/popover/PopoverButton';
import Tick from '../../common/assets/svgIcons/GreenTick';
import NoDataMessage from '../../common/components/NoDataMessage';
import { useNavigate } from 'react-router-dom';
import PulseBoardFullSkeleton from 'Components/feed/components/FullComponentSkeletons/PulseBoardFullSkeleton';
import HeatMapItem from 'Components/common/components/HeatMapItem';
import TriggerBulletWidget from 'Components/common/components/TriggerBulletWidget';




const Wrapper = styled.div`
  height: 670px;
  box-sizing: border-box;
  box-shadow : ${boxShadow};
  background-color: #fff;
  border-radius: 12px;
  padding: 16px 24px;
  @media all and (max-width: ${maxTablet}) {
    padding: 16px 10px;
  }
  @media all and (max-width: ${maxMobile}) {
    height: auto;
    margin-top: 20px;
    padding: 16px 10px 0px;
  }
  .pulse-row {
    @media all and (max-width: ${maxTablet}) {
        height: calc(100% - 70px);
    }
    .pulse-col {
      height: 440px;
      .no-data-msg-container {
        height: 100%;
        justify-content: center;
      }
    }
  }
  .heading-inner-page {
    .tabs-wrapper {
      @media all and (max-width: ${maxTablet}) {
       margin-top: 10px;
      }
    }
    .pulse-filter-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .pulse-filter-item {
      color: ${greyColor};
      font-family: ${primaryFont};
      font-size: 14px;
      font-weight: ${wMedium};
      letter-spacing: -0.4px;
      cursor: pointer;
      &:hover {
        color: ${primaryColor};
      }
      &.active {
        color: ${primaryColor};
      }
      .tick-span {
        position: absolute;
        left: 9px;
      }
    }
    .ant-popover {
      width: auto !important;
      max-width: 100% !important;
      .ant-popover-inner {
        padding: 12px 24px !important;
        .ant-popover-inner-content {
          width: 100% !important;
          height: auto !important;
        }
      }
    }
  }
  .collapse-heading {
    display: flex;
    justify-content: center;
    font-family: ${secondaryFont};
    font-weight: ${wMedium};
    font-size: 14px;
    line-height: 15px;
    color: ${primaryColor};
    cursor: pointer;
    @media all and (max-width: ${maxMobile}) {
      margin-block: 16px;
    }
    .anticon {
      margin-left: 5px;
    }
  }
 .ant-pulse-triggers {
   margin-top: 20px;
    .ant-col-triggers {
      flex: 0 0 25%;
      max-width: 25%;
    }
  }
  .empty-pulse-state {
    height: 95%;
    margin-top: 50%;
  }
`;

const SubHeading = styled.span`
  font-size: 14px;
  font-family: ${primaryFont};
  font-weight: ${wMedium};
  color: #67696A;
  margin-bottom: 16px;
  display: inline-block;
`;

const HeatMapRow = styled.div`
  display: flex;
  height: 7px;
  border-radius: 8px;
  .tooltip-item { 
    &:first-child {   
      border-radius: 5px 0px 0px 5px; 
    }  
    &:last-child {  
      border-radius: 0px 5px 5px 0px; 
    }
  }
`;

const PulseWrap = styled.div`
  &.flexProps{
    display: flex;
    flex-direction: column;
    height: 440px;
    overflow-y: auto;
    &::-webkit-scrollbar {
      width: 5px;
      border-radius:8px;
    }
    &::-webkit-scrollbar-thumb {
      background: #001760;
      border-radius: 12px;
    }
    &::-webkit-scrollbar-track {
      background: #CFDAE8;
      border-radius: 12px;
    }
  }
`;

const pulseTypes = [
  {
    label: '24 Hrs',
    heatMapType: 'heatmap1',
  },
  {
    label: 'One Week',
    heatMapType: 'heatmap7',
  },
  {
    label: 'One Month',
    heatMapType: 'heatmap30',
  },
];
type Props = {
  withoutLeaderBoard?: boolean,
} & typeof defaultProps;

const defaultProps = {
  withoutLeaderBoard: false,
};
const PulseBoard = function PulseBoard(props: Props) {
  const navigate = useNavigate();
  const { withoutLeaderBoard } = props;
  const dispatch = useDispatch();
  const [activePulseType, setActivePulseType] = useState(0);
  const selectedFeedBoardPulseData: CompaniesPulse = useSelector(selectFeedBoardPulseData);
  const selectedFeedBoardPulseLoading: boolean = useSelector(selectFeedBoardPulseLoading);
  const [hasFetchedPulse, setHasFetchedPulse] = useState(false);
  const isMob = IsMobile();
  const [collapse, setCollapse] = useState(false);
  const flagForLeaderBoard: boolean = (withoutLeaderBoard && !isMob);
  const selectedLabel = pulseTypes[activePulseType].label;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setHasFetchedPulse(true);
    dispatch(feedActions.getFeedPulseBoard({}));
  }, []);

  const {
    title = 'Pulse', data: { alerts } = {},
  } = selectedFeedBoardPulseData;

  const getHeatMapComponent = (alertsFromHeatMap: object) => {
    const HeatMapTools: ReactElement[] = [];
    forOwn(
      alertsFromHeatMap,
      (value, key) => {
        const filteredAlerts = alerts?.filter((alert) => `alert${alert.alertId}` === key);
        let color = '';
        let tip = '';

        if (filteredAlerts?.length) {
          color = filteredAlerts[0].color;
          tip = `${filteredAlerts[0].alert} (${(value * 1).toFixed(2)}%)`;
        }

        if (tip && color && value) {
          HeatMapTools.push(
            <HeatMapTooltipItem
              tooltipText={tip}
              color={color}
              width={`${value * 1}%`}
            />,
          );
        }
      },
    );
    return HeatMapTools;
  };

  const { heatMapType } = pulseTypes[activePulseType];
  const activeHeatMap = selectedFeedBoardPulseData?.data ? selectedFeedBoardPulseData?.data[
    `${heatMapType}` as keyof typeof selectedFeedBoardPulseData.data
  ] as Heatmap[] : [] as Heatmap[];

  const data = useSelector(selectData);

  const handleClick = (index: number) => {
    if (index !== activePulseType) {
      dispatch(certificationActions.certificationAction({
        type: 'pulse_filter',
        source: 'pulse',
        data,
      }));
    }
    setActivePulseType(index);
    setOpen(false);
  };

  const PulseFilterContent = () => (
    <div className="pulse-filter-content">
      {pulseTypes.map((pulse, index) => (
        <div
          key={index}
          className={classNames('pulse-filter-item', { active: activePulseType === index })}
          onClick={() => handleClick(index)}
          role="button"
          tabIndex={0}
        >
          {activePulseType === index ? <span className="tick-span"><Tick /></span> : null}
          {pulse.label}
        </div>
      ))}
    </div>
  );

  const hasAnyHeatmapData = (() => {
    const data = selectedFeedBoardPulseData?.data;
    if (!data) return false;

    return (
      (data.heatmap1 && data.heatmap1.length > 0) ||
      (data.heatmap7 && data.heatmap7.length > 0) ||
      (data.heatmap30 && data.heatmap30.length > 0)
    );
  })();

  return (
    <>
      {selectedFeedBoardPulseLoading || !hasFetchedPulse ? (
        <PulseBoardFullSkeleton />
      ) : (
        <Wrapper className={classNames('pulse-wrapper', { withoutLeaderBoardClass: flagForLeaderBoard })}>
          <div className="heading-inner-page mb-c-10" id="certification-activity">
            <h1 className="heading d-flex align-items-center">
              {title}
              <HeadingWithToolTip
                heightIconXSm="17px"
                toolTipText={TOOLTIP_PULSE}
                videoLink={VIDEO_PULSE}
              />
            </h1>
            {hasAnyHeatmapData &&
              <div className="tabs-wrapper">
                <PopoverButton
                  variant="iconTextDropdown"
                  text={selectedLabel}
                  content={PulseFilterContent()}
                  placement="bottomRight"
                  open={open}
                  setOpen={setOpen}
                  isActive={activePulseType !== 0}
                />
              </div>
            }
          </div>
          {selectedFeedBoardPulseData ? (
            <Row className="pulse-row">
              <Col span={24} className="pulse-col">
                {activeHeatMap.length > 0 ? (
                  <>
                    <SubHeading className="account-text">Accounts sorted by Sales Trigger activity</SubHeading>
                    <PulseWrap
                      className={classNames('pulse-wrap', {
                        flexProps: activeHeatMap?.length > 8,
                      })}
                    >
                      {activeHeatMap.map(({
                        name, id, alerts: alertsFromHeatMap, articlesCount,
                      }) => (
                        <HeatMapItem heading={name || ''} count={articlesCount} id={id}>
                          <HeatMapRow className="heatmap-row">
                            {alertsFromHeatMap ? getHeatMapComponent(alertsFromHeatMap) : null}
                          </HeatMapRow>
                        </HeatMapItem>
                      ))}
                    </PulseWrap>
                  </>
                ) :
                  <NoDataMessage
                    title="You’re almost there!"
                    subtitle="Just follow a few more companies."
                    btnText="Follow Companies"
                    redirectTo={() => { navigate('/companies') }}
                  />
                }
              </Col>
              {activeHeatMap.length > 0 && (
                <Col span={24} className="triggers-col">
                  {flagForLeaderBoard ? (
                    <SubHeading>Sales Triggers</SubHeading>
                  ) : null}

                  {isMob && !collapse ? (
                    <span
                      tabIndex={0}
                      role="button"
                      onClick={() => setCollapse(true)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setCollapse(true);
                        }
                      }}
                      className="collapse-heading"
                    >
                      View Triggers
                      <CaretDownOutlined />
                    </span>
                  ) : null}

                  {(!isMob || (isMob && collapse)) && (
                    <Row className="ant-pulse-triggers">
                      {alerts && alerts?.slice(0, alerts?.length).map(({ alert, color }, index) => (
                        <Col className="ant-col-triggers" key={`${alert}-${index}`}>
                          <TriggerBulletWidget color={color} title={alert} showTooltip />
                        </Col>
                      ))
                      }
                    </Row>
                  )}

                  {isMob && collapse && (
                    <span
                      tabIndex={0}
                      role="button"
                      onClick={() => setCollapse(false)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setCollapse(false);
                        }
                      }}
                      className="collapse-heading"
                    >
                      Hide
                      <CaretUpOutlined />
                    </span>
                  )}
                </Col>
              )}
            </Row>
          ) : (
            <div className="empty-pulse-state">
              <NoDataMessage
                title="You’re almost there!"
                subtitle="Just follow a few more companies."
                btnText="Follow Companies"
                redirectTo={() => { navigate('/companies') }}
              />
            </div>
          )}
        </Wrapper>
      )}
    </>
  );
};
PulseBoard.defaultProps = defaultProps;
export default PulseBoard;
